// Pure helpers for LopDateInput. The component's value contract is the same as the
// native <input type="date">: 'yyyy-MM-dd' or '' - never a Date, never a timezone.

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

export const MIN_YEAR = 1900;
export const MAX_YEAR = 2200;

export function daysInMonth(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

export function isRealDate(y: number, m: number, d: number): boolean {
  return (
    Number.isInteger(y) && Number.isInteger(m) && Number.isInteger(d) &&
    y >= MIN_YEAR && y <= MAX_YEAR && m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m)
  );
}

export function toIso(y: number, m: number, d: number): string {
  return `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
}

/** 'yyyy-MM-dd' -> 'dd/MM/yyyy' ('' when empty or not a real date). */
export function isoToDisplay(iso: string | null | undefined): string {
  const p = isoParts(iso);
  return p ? `${pad(p.d)}/${pad(p.m)}/${p.y}` : '';
}

export function isoParts(iso: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  return isRealDate(y, mo, d) ? { y, m: mo, d } : null;
}

/** Parse what a person may type: "20/10/2026", "20.10.2026", "20-10-2026", "20102026",
 *  also "2026-10-20". Returns 'yyyy-MM-dd', '' for blank, or null when invalid. */
export function parseTypedDate(text: string): string | '' | null {
  const s = text.trim();
  if (s === '') return '';
  let y: number, m: number, d: number;
  let r: RegExpExecArray | null;
  if ((r = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s))) {
    y = Number(r[1]); m = Number(r[2]); d = Number(r[3]);
  } else if ((r = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s))) {
    d = Number(r[1]); m = Number(r[2]); y = Number(r[3]);
  } else if ((r = /^(\d{2})(\d{2})(\d{4})$/.exec(s))) {
    d = Number(r[1]); m = Number(r[2]); y = Number(r[3]);
  } else {
    return null;
  }
  return isRealDate(y, m, d) ? toIso(y, m, d) : null;
}

export function isoOutOfRange(iso: string, min?: string, max?: string): boolean {
  return (!!min && iso < min) || (!!max && iso > max);
}

/** Add days to an ISO date (UTC arithmetic - no DST surprises). */
export function addDays(iso: string, delta: number): string {
  const p = isoParts(iso)!;
  const dt = new Date(Date.UTC(p.y, p.m - 1, p.d + delta));
  return toIso(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

/** Add months, clamping the day to the target month's length. */
export function addMonths(iso: string, delta: number): string {
  const p = isoParts(iso)!;
  const idx = p.y * 12 + (p.m - 1) + delta;
  const y = Math.floor(idx / 12);
  const m = (idx % 12) + 1;
  return toIso(y, m, Math.min(p.d, daysInMonth(y, m)));
}

/** Local "today" as ISO (the user's calendar day - what a date picker highlights). */
export function todayIso(now: Date = new Date()): string {
  return toIso(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** Cells for a month grid, week starting Sunday: 6 rows x 7, ISO strings. */
export function monthGrid(year: number, month1: number): string[] {
  const first = new Date(Date.UTC(year, month1 - 1, 1)).getUTCDay(); // 0 = Sunday
  const start = addDays(toIso(year, month1, 1), -first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}
